import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import "./MonitoringPage.css";

const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b > 1e12) return (b / 1e12).toFixed(2) + " TB";
  if (b > 1e9) return (b / 1e9).toFixed(2) + " GB";
  if (b > 1e6) return (b / 1e6).toFixed(2) + " MB";
  return b + " B";
};

const fmtUptime = (s) => {
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
};

function Bar({ percent, label }) {
  const cls = percent > 85 ? "danger" : percent > 65 ? "warn" : "ok";
  return (
    <div className="mn-bar-wrap">
      <div className="mn-bar-label">
        <span>{label}</span>
        <span>{percent != null ? percent.toFixed(0) + "%" : "—"}</span>
      </div>
      <div className="mn-bar"><div className={`mn-bar-fill ${cls}`} style={{ width: `${Math.min(percent || 0, 100)}%` }} /></div>
    </div>
  );
}

function ServerCard({ title, data, icon }) {
  const status = data?.status || "loading";
  const offline = status !== "online";
  const isEmail = Array.isArray(data?.services);
  return (
    <div className={`mn-card ${status}`}>
      <div className="mn-card-head">
        <span className="mn-icon">{icon}</span>
        <h2>{title}</h2>
        <span className={`mn-badge ${status}`}>
          {status === "online" ? "● Online" :
           status === "offline" ? "● Offline" :
           status === "not_configured" ? "Nicht konfiguriert" : "…"}
        </span>
      </div>
      {data?.host && <div className="mn-host">{data.host}</div>}
      {offline && data?.error && <div className="mn-err">{data.error}</div>}
      {!offline && isEmail && (
        <>
          {(data.services || []).map((s, i) => (
            <div key={i} className="mn-svc">
              <span className={`mn-svc-dot ${s.ok ? "ok" : "danger"}`} />
              <strong>{s.name}</strong>
              <span className="mn-svc-port">:{s.port}</span>
              <span className="mn-svc-meta">{s.ok ? `${s.latency_ms} ms` : (s.error || "Fehler")}</span>
            </div>
          ))}
        </>
      )}
      {!offline && !isEmail && (
        <>
          {data.cpu_percent != null && <Bar percent={data.cpu_percent} label="CPU" />}
          {(data.mem_percent != null || data.ram_percent != null) && (
            <Bar
              percent={data.mem_percent ?? data.ram_percent}
              label={`RAM (${fmtBytes(((data.mem_used_gb ?? data.ram_used_gb) || 0) * 1e9)} / ${fmtBytes(((data.mem_total_gb ?? data.ram_total_gb) || 0) * 1e9)})`}
            />
          )}
          {data.disks && data.disks.slice(0, 3).map((d, i) => (
            <Bar key={i} percent={d.percent} label={`Disk ${d.mount} (${fmtBytes(d.free_gb * 1e9)} frei)`} />
          ))}
          {data.uptime_sec != null && data.uptime_sec > 0 && <div className="mn-meta">Uptime: {fmtUptime(data.uptime_sec)}</div>}
          {data.uptime_h != null && <div className="mn-meta">Uptime: {data.uptime_h} h</div>}
          {data.loadavg_1m != null && <div className="mn-meta">Load 1m: {data.loadavg_1m}</div>}
          {data.idle && <div className="mn-pill">💤 Ruhezustand</div>}
        </>
      )}
    </div>
  );
}

export default function MonitoringPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [updated, setUpdated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let unterwegs = false;   // nicht nachfragen, solange die letzte Antwort fehlt (sonst stauen sich Anfragen)
    const tick = async () => {
      if (unterwegs) return;
      unterwegs = true;
      try {
        const r = await fetch(`${API_BASE}/monitoring/all`);
        const d = await r.json();
        if (!cancelled) { setData(d); setUpdated(new Date()); }
      } catch (e) { /* */ }
      finally { unterwegs = false; }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div className="mn-page">
      <div className="mn-header">
        <button className="mn-back" onClick={() => navigate("/")}>← Home</button>
        <h1>📊 Server-Monitoring</h1>
        <div className="mn-updated">
          {updated ? "Aktualisiert: " + updated.toLocaleTimeString() : "Lade…"}
        </div>
      </div>
      <div className="mn-grid">
        <ServerCard
          title={data.local && /macOS|Darwin/i.test(data.local.platform || "") ? "Lokal (Backend)" : "Server (Backend)"}
          icon="💻"
          data={data.local}
        />
        <ServerCard title="Synology NAS" icon="🗄️" data={data.synology} />
        <ServerCard title="OCR-Server" icon="🌐" data={data.ocr} />
        <ServerCard title="Email-Server" icon="✉️" data={data.email} />
      </div>
    </div>
  );
}
